import { pixelRatio } from "../../util/DeviceUtil";
import { RenderStats } from "./RenderStats";

function resizeCanvas(canvas: HTMLCanvasElement) {
    const devicePixelRatio = pixelRatio;
    const width = canvas.offsetWidth * devicePixelRatio;
    const height = canvas.offsetHeight * devicePixelRatio;

    if (width !== canvas.width || height !== canvas.height) {
        canvas.width = width;
        canvas.height = height;
        return true;
    }

    return false;
}

export abstract class Renderer {
    canvas: HTMLCanvasElement;
    overlayCanvas: HTMLCanvasElement;

    animationId: number | undefined;
    running: boolean = false;

    fpsLimit: number = 999;

    stats: RenderStats = new RenderStats();

    constructor() {
        this.canvas = this.createCanvas("canvas");
        this.overlayCanvas = this.createCanvas("overlayCanvas");
        this.overlayCanvas.style.pointerEvents = "none";
    }

    abstract init(): Promise<void>;

    abstract cleanUp(): void;

    private createCanvas(name: string): HTMLCanvasElement {
        const canvas = document.createElement("canvas");
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.tabIndex = 0;
        canvas.style.position = "absolute";
        return canvas;
    }

    start() {
        this.running = true;
        this.animationId = requestAnimationFrame(this.frameCallback);
    }

    stop() {
        this.running = false;
        if (this.animationId !== undefined) {
            cancelAnimationFrame(this.animationId);
            this.animationId = undefined;
        }
        this.cleanUp();
    }

    onResize(width: number, height: number) {}

    frameCallback = (time: DOMHighResTimeStamp) => {
        try {
            const resized = resizeCanvas(this.canvas) && resizeCanvas(this.overlayCanvas);
            if (resized) {
                this.onResize(this.canvas.width, this.canvas.height);
            }

            const deltaTime = this.stats.getDeltaTime(time);

            if (this.fpsLimit && deltaTime > 0) {
                const tolerance = 1;
                if (deltaTime < 1000 / this.fpsLimit - tolerance) {
                    return;
                }
            }

            this.stats.update(time);

            this.render(time, deltaTime, resized);

            this.onFrameEnd();
        } finally {
            if (this.running) {
                this.animationId = requestAnimationFrame(this.frameCallback);
            }
        }
    };

    abstract render(
        time: DOMHighResTimeStamp,
        deltaTime: DOMHighResTimeStamp,
        resized: boolean,
    ): void;

    onFrameEnd() {
        this.stats.onFrameEnd();
    }
}
